import { Component, inject, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  IdentityServiceBase,
  RegisterAccountModel,
  RegisterResponse,
  RegisterUserModel,
} from '@seahorse/auth';
import { NotificationService, ResultStatus } from '@seahorse/common';
import { MfaSetupCode } from '@seahorse/domain';
import { SeahorseFormBuilder } from '@seahorse/forms';
import { MfaService, MfaSetupComponent } from '@seahorse/ui';
import { Subscription } from 'rxjs';
import { InvitationLoginModel } from '../models/login.model';
import { PortalAccountService } from '../services/user-account.service';
import { createLoginForm } from './login.form';
import { createRegistrationForm } from './registration.form';

@Component({
  selector: 'cp-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage {
  @ViewChild('mfaSetup', { static: false })
  mfaSetupComponent?: MfaSetupComponent;

  verificationCode = inject(SeahorseFormBuilder).nonNullable.control(
    '',
    Validators.required
  );
  mfaSetupCode?: MfaSetupCode;

  currentForm: FormType = FormType.Login;
  formType = FormType;

  isLoading = false;
  showVerificationError = false;
  isUnauthorized = false;

  unauthorizedMessage;

  loginForm = createLoginForm();
  registrationForm = createRegistrationForm();
  registerResponse: RegisterResponse;

  private _subscriptions = new Subscription();
  private _accountService = inject(PortalAccountService);
  private _router = inject(Router);
  private _mfaService = inject(MfaService);
  private _identityService = inject(IdentityServiceBase);
  private _notificationService = inject(NotificationService);
  private _route = inject(ActivatedRoute);
  private _translateService = inject(TranslateService);

  private _invitationToken = this._route.snapshot.queryParams['invitation'];

  login() {
    this.loginForm.submit();

    if (!this.loginForm.valid) {
      return;
    }

    const value = this.loginForm.getRawValue();

    const model = new InvitationLoginModel();
    model.password = value.password as string;
    model.username = value.username as string;
    model.rememberMe = value.rememberMe;
    model.invitation = this._invitationToken;

    this.isLoading = true;

    this._subscriptions.add(
      this._accountService.login(model).subscribe(
        (response) => {
          this.isLoading = false;
          if (response.isSuccess) {
            if (!response.isMfaEnabled && response.isMfaRequired) {
              this._mfaService.initMfa().subscribe((res) => {
                this.mfaSetupCode = res.result;
                this.currentForm = FormType.SetupMFA;
              });
            } else if (response.isMfaEnabled) {
              this.currentForm = FormType.VerifyMFA;
            } else {
              this.toDashboard();
            }
          }

          if (
            response?.isUnauthorized &&
            response?.status == ResultStatus.NotPermitted
          ) {
            this.unauthorizedMessage = this._translateService.instant(
              'login.error.uninvited'
            );
            this.isUnauthorized = true;
          }
        },
        (error) => {
          this.isLoading = false;
          if (error.status === 401) {
            this.isUnauthorized = true;
            this.unauthorizedMessage = this._translateService.instant(
              'login.error.incorrectCredentials'
            );
          }
        }
      )
    );
  }

  register() {
    this.registrationForm.submit();
    const termsAccepted = this.registrationForm.controls.terms?.value;

    if (!termsAccepted) {
      this._notificationService.showError('account.register.error.acceptTerms');
      return;
    }

    if (!this.registrationForm.valid) {
      return;
    }

    this.isLoading = true;

    const registerModel = new RegisterAccountModel();
    const user: RegisterUserModel = this.registrationForm.getRawValue();
    user.username = user.emailAddress;

    registerModel.companyName =
      this.registrationForm.controls.companyName?.value;
    registerModel.users = [user];

    const languageCode = this._translateService.getDefaultLang();

    this._accountService
      .registerAccount(registerModel, this._invitationToken, languageCode)
      .subscribe(
        (response) => {
          if (response.hasResult && response.status === ResultStatus.OK) {
            this._notificationService.showSuccess('shared.terms.success');

            this.isLoading = false;
            this.registerResponse = null;
            this._router.navigate(['account/register/thank-you'], {
              queryParamsHandling: 'merge',
            });
          } else {
            this.registerResponse = this._accountService.getRegistrationErrors(
              response.messages
            );
            this.isLoading = false;
          }
        },
        (error) => {
          this.registerResponse =
            this._accountService.getRegistrationErrors(error);
          this.isLoading = false;
        }
      );
  }

  sendVerificationCode(code: string) {
    if (!code) {
      this.showVerificationError = true;
      return;
    }

    this.isLoading = true;
    this._subscriptions.add(
      this._accountService.verifyMfa(code).subscribe((response) => {
        this.isLoading = false;
        if (response.isSuccess) {
          this._router.navigate(['/']);
        } else {
          this.showVerificationError = true;
        }
      })
    );
  }

  completeMfa() {
    if (!this.mfaSetupComponent) {
      return;
    }

    const code = this.mfaSetupComponent.codeControl.getRawValue();

    if (!code.length) {
      this._notificationService.showError('user.mfa.verificationCodeRequired');

      return;
    }

    this.isLoading = true;
    this._subscriptions.add(
      this._mfaService.completeMfa(code, true).subscribe((res) => {
        this.isLoading = false;
        if (res.result) {
          this._identityService.isMfaEnabled = true;
          this._router.navigate(['/']);
        } else {
          this.showVerificationError = true;
        }
      })
    );
  }

  backToLogin() {
    this.currentForm = FormType.Login;
    this.showVerificationError = false;
  }

  toDashboard() {
    this._router.navigate(['/dashboard'], { queryParamsHandling: 'merge' });
  }
}

export enum FormType {
  Login,
  ForgotPassword,
  SetupMFA,
  VerifyMFA,
  Register,
}
